import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { Icon } from '@iconify/react';
import SidebarContext from '../../contexts/MyContext';
import Loader from '../../animations/Loader';
import { AudioHandler } from '../../Apihander/AudioRecording/AudioRecording';
import { errorToast, successToast } from '../../utils/notificationUtils';
import { formatTimeInHHMMSS, formatTimeWithOptionalHours } from '../../utils/timeFile';
import {
    GAIN_MIN_DB, GAIN_MAX_DB, SPEED_PRESETS, DEFAULT_ADJUST_SETTINGS,
    buildEffectChain, renderAdjustedBuffer, audioBufferToWav, audioBufferToMp3,
} from '../../utils/adjustAudioUtils';
import '../../css/adjust.css';

const DIAL_SIZE = 180;
const DIAL_RADIUS = 74;
const DIAL_SWEEP = 135;
const RULER_STEP = 3;
const RULER_TICKS = Array.from({ length: (GAIN_MAX_DB - GAIN_MIN_DB) / RULER_STEP + 1 }, (_, i) => GAIN_MIN_DB + i * RULER_STEP);
const RULER_LABELS = [-12, 0, 12, 24, 36];

const dbToAngle = (db) => -DIAL_SWEEP + ((db - GAIN_MIN_DB) / (GAIN_MAX_DB - GAIN_MIN_DB)) * DIAL_SWEEP * 2;
const angleToPoint = (deg) => {
    const rad = (deg * Math.PI) / 180;
    return { x: DIAL_SIZE / 2 + DIAL_RADIUS * Math.sin(rad), y: DIAL_SIZE / 2 - DIAL_RADIUS * Math.cos(rad) };
};
const arcPath = (fromDeg, toDeg) => {
    const a = angleToPoint(fromDeg);
    const b = angleToPoint(toDeg);
    const largeArc = toDeg - fromDeg > 180 ? 1 : 0;
    return `M ${a.x} ${a.y} A ${DIAL_RADIUS} ${DIAL_RADIUS} 0 ${largeArc} 1 ${b.x} ${b.y}`;
};
const formatDb = (db) => (db > 0 ? `+${db}` : `${db}`);
const formatSpeed = (speed) => `${Number(speed.toFixed(2))}x`;

const AdjustAudio = ({ audioName, audioUrl, audioId, folderId, isLocalUpload, isPrevDisabled, isNextDisabled, playPreviousTrack, playNextTrack }) => {
    const { selectedFormat, setActionTabIndex, setAudio, setFilterAudioData, setSelectedAdjustAudio, setTabIndex, audioData, filterAudioData, favoriteAudio } = useContext(SidebarContext);

    const containerRef = useRef(null);
    const audioRef = useRef(null);
    const wavesurferRef = useRef(null);
    const audioCtxRef = useRef(null);
    const sourceNodeRef = useRef(null);
    const chainOutputRef = useRef(null);
    const dialRef = useRef(null);
    const isMountedRef = useRef(true);

    const [settings, setSettings] = useState(DEFAULT_ADJUST_SETTINGS);
    const [previewMode, setPreviewMode] = useState('adjusted');
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const hasChanges = settings.noiseReduction || settings.enhance || settings.gainDb !== 0 || settings.speed !== 1;
    const effectiveSpeed = previewMode === 'adjusted' ? settings.speed : 1;

    const updateSetting = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

    // Waveform + playback are driven by our own <audio> so it can run through the effect chain.
    useEffect(() => {
        if (!containerRef.current || !audioRef.current || !audioUrl) return;

        setIsLoading(true);
        setError(null);
        setCurrentTime(0);
        setDuration(0);
        audioRef.current.crossOrigin = 'anonymous';

        const ws = WaveSurfer.create({
            container: containerRef.current,
            media: audioRef.current,
            url: audioUrl,
            height: 64,
            waveColor: '#CBD5E1',
            progressColor: '#94A3B8',
            cursorColor: '#334155',
            cursorWidth: 2,
            barWidth: 3,
            barGap: 3,
            barRadius: 3,
        });

        ws.on('ready', (d) => {
            setDuration(d);
            setIsLoading(false);
        });
        ws.on('timeupdate', (t) => setCurrentTime(t));
        ws.on('play', () => setIsPlaying(true));
        ws.on('pause', () => setIsPlaying(false));
        ws.on('finish', () => setIsPlaying(false));
        ws.on('error', (err) => {
            setIsLoading(false);
            setError(`Unable to load audio: ${err?.message || err}`);
        });

        wavesurferRef.current = ws;

        return () => {
            ws.destroy();
            wavesurferRef.current = null;
        };
    }, [audioUrl]);

    useEffect(() => () => {
        isMountedRef.current = false;
        if (audioCtxRef.current) audioCtxRef.current.close();
    }, []);

    // Re-wire the live effect chain whenever a setting or the preview mode changes.
    const connectGraph = useCallback(() => {
        const ctx = audioCtxRef.current;
        const source = sourceNodeRef.current;
        if (!ctx || !source) return;

        source.disconnect();
        if (chainOutputRef.current) chainOutputRef.current.disconnect();

        if (previewMode === 'adjusted') {
            const chain = buildEffectChain(ctx, settings);
            source.connect(chain.input);
            chain.output.connect(ctx.destination);
            chainOutputRef.current = chain.output;
        } else {
            source.connect(ctx.destination);
            chainOutputRef.current = null;
        }
    }, [settings, previewMode]);

    useEffect(() => {
        connectGraph();
    }, [connectGraph]);

    useEffect(() => {
        if (wavesurferRef.current && !isLoading) {
            wavesurferRef.current.setPlaybackRate(effectiveSpeed, true);
        }
    }, [effectiveSpeed, isLoading]);

    const ensureAudioGraph = () => {
        if (!audioCtxRef.current) {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            audioCtxRef.current = ctx;
            sourceNodeRef.current = ctx.createMediaElementSource(audioRef.current);
            connectGraph();
        }
        if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
    };

    const onPlayPause = () => {
        if (!wavesurferRef.current || isLoading) return;
        ensureAudioGraph();
        wavesurferRef.current.playPause();
    };

    const skip = (seconds) => {
        const ws = wavesurferRef.current;
        if (!ws || isLoading) return;
        ws.setTime(Math.max(0, Math.min(duration, ws.getCurrentTime() + seconds)));
    };

    // Dial drag: angle from the centre maps to dB.
    const setGainFromPointer = (event) => {
        const rect = dialRef.current.getBoundingClientRect();
        const scale = DIAL_SIZE / rect.width;
        const x = (event.clientX - rect.left) * scale - DIAL_SIZE / 2;
        const y = (event.clientY - rect.top) * scale - DIAL_SIZE / 2;
        const angle = Math.max(-DIAL_SWEEP, Math.min(DIAL_SWEEP, (Math.atan2(x, -y) * 180) / Math.PI));
        const db = GAIN_MIN_DB + ((angle + DIAL_SWEEP) / (DIAL_SWEEP * 2)) * (GAIN_MAX_DB - GAIN_MIN_DB);
        updateSetting('gainDb', Math.round(db));
    };

    const onDialPointerDown = (event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        setGainFromPointer(event);
    };

    const onDialPointerMove = (event) => {
        if (event.buttons === 1) setGainFromPointer(event);
    };

    // Runs the source audio through the effect chain and returns the adjusted buffer.
    const renderAdjusted = async () => {
        const response = await fetch(audioUrl);
        if (!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);
        const arrayBuffer = await response.arrayBuffer();

        const decodeCtx = new (window.AudioContext || window.webkitAudioContext)();
        const buffer = await decodeCtx.decodeAudioData(arrayBuffer);
        decodeCtx.close();

        await new Promise((resolve) => setTimeout(resolve, 0));
        return renderAdjustedBuffer(buffer, settings);
    };

    // Renders the adjusted audio to a WAV file ready for upload.
    const buildAdjustedFile = async (fileName) => {
        const rendered = await renderAdjusted();
        return { file: new File([await audioBufferToWav(rendered)], fileName), duration: rendered.duration };
    };

    // "name.mp3" -> "name(1).mp3", bumping the number until it's unused in the loaded lists.
    const nextAdjustedName = () => {
        const dot = audioName?.lastIndexOf('.') ?? -1;
        let base = dot === -1 ? (audioName || 'Adjusted') : audioName.slice(0, dot);
        const extension = dot === -1 ? (selectedFormat || 'wav') : audioName.slice(dot + 1);
        const suffixRegex = /\((\d+)\)$/;
        const match = base.match(suffixRegex);
        let suffix = 1;

        if (match) {
            suffix = parseInt(match[1], 10) + 1;
            base = base.replace(suffixRegex, '');
        }

        const taken = new Set([...(audioData || []), ...(filterAudioData || []), ...(favoriteAudio || [])].map((file) => file?.audio?.fileName));
        let name = `${base}(${suffix}).${extension}`;
        while (taken.has(name)) {
            suffix += 1;
            name = `${base}(${suffix}).${extension}`;
        }
        return name;
    };

    // Save always creates a new recording; the original is left untouched.
    const saveAsNew = async () => {
        const fileName = nextAdjustedName();
        try {
            setIsSaving(true);
            if (isPlaying) wavesurferRef.current?.pause();

            const { file, duration: adjustedDuration } = await buildAdjustedFile(fileName);

            // The upload route keeps `action` verbatim, so the list shows the adjust icon.
            const formData = new FormData();
            formData.append('audiofile', file, fileName);
            formData.append('timeDuration', formatTimeInHHMMSS(adjustedDuration));
            formData.append('action', 'SpeedController');
            formData.append('isFavourite', false);
            formData.append('isRename', 'true');

            const data = await AudioHandler.doUploadAudioUrl(formData, folderId || null);

            if (data?.statusCode === 200 || data?.status) {
                successToast('Audio adjusted successfully.');
                setFilterAudioData((prev) => [data.data, ...prev]);
                // The screen stays interactive while saving, so it is only closed if the user is still on it.
                if (isMountedRef.current) {
                    setActionTabIndex(null);
                    setAudio();
                }
            } else {
                errorToast(data?.response?.data?.error || data?.message || 'Unable to save adjusted audio.');
            }
        } catch (err) {
            console.log('🚀 ~ uploadAdjusted ~ error:', err);
            errorToast(err?.message || 'Unable to save adjusted audio.');
        } finally {
            if (isMountedRef.current) setIsSaving(false);
        }
    };

    // A file picked in AI Tools was never uploaded, so the adjusted audio is saved as a brand new recording.
    const saveLocalAsNew = async () => {
        const dot = audioName?.lastIndexOf('.') ?? -1;
        const base = dot > 0 ? audioName.slice(0, dot) : (audioName || 'Adjusted');

        try {
            setIsSaving(true);
            if (isPlaying) wavesurferRef.current?.pause();

            const rendered = await renderAdjusted();
            const fileName = `${base}.mp3`;
            const uploadBlob = await audioBufferToMp3(rendered);

            const formData = new FormData();
            formData.append('audiofile', new File([uploadBlob], fileName, { type: 'audio/mpeg' }), fileName);
            formData.append('timeDuration', formatTimeInHHMMSS(rendered.duration));
            formData.append('action', 'SpeedController');
            formData.append('isFavourite', false);
            formData.append('hasnote', 'false');
            // Lets the backend suffix the name instead of rejecting a duplicate.
            formData.append('isRename', 'true');

            const data = await AudioHandler.doUploadAudioUrl(formData);

            if (data?.statusCode === 200 || data?.status) {
                successToast(data?.message || 'Audio adjusted successfully.');
                setFilterAudioData((prev) => [data.data, ...prev]);
                URL.revokeObjectURL(audioUrl);
                if (isMountedRef.current) {
                    setSelectedAdjustAudio([]);
                    setActionTabIndex(null);
                    setAudio();
                    // The saved recording lives in My Recordings, the same tab the other AI tools land on.
                    setTabIndex(2);
                }
            } else {
                errorToast(data?.response?.data?.error || data?.message || 'Unable to save adjusted audio.');
            }
        } catch (err) {
            console.log('🚀 ~ saveLocalAsNew ~ error:', err);
            errorToast(err?.message || 'Unable to save adjusted audio.');
        } finally {
            if (isMountedRef.current) setIsSaving(false);
        }
    };

    const knob = angleToPoint(dbToAngle(settings.gainDb));
    const speedPercent = ((settings.speed - 0.5) / 1.5) * 100;

    return (
        <div className="adjust-panel">
            <audio ref={audioRef} preload="auto" style={{ display: 'none' }} />

            <div className="adjust-card full">
                <div className="adjust-card-head">
                    <h6 className="adjust-card-title">Audio Preview</h6>
                    <div className="adjust-segment">
                        <button className={previewMode === 'original' ? 'active' : ''} onClick={() => setPreviewMode('original')}>Original</button>
                        <button className={previewMode === 'adjusted' ? 'active' : ''} onClick={() => setPreviewMode('adjusted')}>Adjusted</button>
                    </div>
                </div>

                <div className="adjust-wave" ref={containerRef} />
                {error && <p className="adjust-error">{error}</p>}

                <div className="adjust-preview-bar">
                    <span className="adjust-time">{formatTimeWithOptionalHours(currentTime)}</span>

                    <div className="adjust-transport">
                        <button className="adjust-icon-btn is-nav" disabled={isLoading || isPrevDisabled} onClick={playPreviousTrack} title="Previous">
                            <img src={isLoading || isPrevDisabled ? "/img/dashboard/disabled_previous.png" : "/img/dashboard/previous.png"} width="30px" height="30px" alt="prev" />
                        </button>
                        <button className="adjust-icon-btn" disabled={isLoading} onClick={() => skip(-5)} title="Back 5 seconds">
                            <img src="/img/trim/backward.png" alt="Backward" />
                        </button>
                        <button className={`adjust-play${isPlaying ? ' is-playing' : ''}`} disabled={isLoading} onClick={onPlayPause} aria-label={isPlaying ? "Pause" : "Play"}>
                            {isLoading ? <Loader /> : (
                                <img src={isPlaying ? "/img/trim/pause.png" : "/img/trim/play.png"} alt={isPlaying ? "Pause" : "Play"} className={isPlaying ? "" : "is-play"} />
                            )}
                        </button>
                        <button className="adjust-icon-btn" disabled={isLoading} onClick={() => skip(5)} title="Forward 5 seconds">
                            <img src="/img/trim/forward.png" alt="Forward" />
                        </button>
                        <button className="adjust-icon-btn is-nav" disabled={isLoading || isNextDisabled} onClick={playNextTrack} title="Next">
                            <img src={isLoading || isNextDisabled ? "/img/dashboard/disabled_next.png" : "/img/dashboard/next.png"} width="30px" height="30px" alt="next" />
                        </button>
                    </div>

                    <span className="adjust-time is-end">{formatTimeWithOptionalHours(duration)}</span>
                </div>
            </div>

            <div className="adjust-card">
                <div className="adjust-row">
                    <div className="adjust-row-icon"><Icon icon="lucide:audio-waveform" width="20" height="20" /></div>
                    <div className="adjust-row-text">
                        <h6 className="adjust-card-title">Noise Reduction</h6>
                        <p className="adjust-card-subtitle">Remove background noise</p>
                    </div>
                    <label className="adjust-switch">
                        <input type="checkbox" checked={settings.noiseReduction} onChange={(e) => updateSetting('noiseReduction', e.target.checked)} />
                        <span />
                    </label>
                </div>
            </div>

            <div className="adjust-card">
                <div className="adjust-row">
                    <div className="adjust-row-icon"><Icon icon="lucide:sparkles" width="20" height="20" /></div>
                    <div className="adjust-row-text">
                        <h6 className="adjust-card-title">Enhance Recording</h6>
                        <p className="adjust-card-subtitle">Improve voice quality</p>
                    </div>
                    <label className="adjust-switch">
                        <input type="checkbox" checked={settings.enhance} onChange={(e) => updateSetting('enhance', e.target.checked)} />
                        <span />
                    </label>
                </div>
            </div>

            <div className="adjust-card">
                <h6 className="adjust-card-title">Volume Booster</h6>
                <p className="adjust-card-subtitle">Increase or decrease output level</p>

                <div className="adjust-dial-wrap">
                    <Icon className="adjust-side-icon" icon="lucide:volume-1" width="22" height="22" />
                    <svg
                        ref={dialRef}
                        className="adjust-dial"
                        width={DIAL_SIZE}
                        height={DIAL_SIZE * 0.88}
                        viewBox={`0 0 ${DIAL_SIZE} ${DIAL_SIZE * 0.88}`}
                        onPointerDown={onDialPointerDown}
                        onPointerMove={onDialPointerMove}
                    >
                        <defs>
                            <linearGradient id="adjustGainGrad" x1="0" y1="1" x2="1" y2="0">
                                <stop offset="0%" stopColor="#60A5FA" />
                                <stop offset="100%" stopColor="#2563EB" />
                            </linearGradient>
                        </defs>
                        <path className="adjust-dial-track" d={arcPath(-DIAL_SWEEP, DIAL_SWEEP)} fill="none" stroke="#E2E8F0" strokeWidth="10" strokeLinecap="round" />
                        {settings.gainDb > GAIN_MIN_DB && (
                            <path className="adjust-dial-fill" d={arcPath(-DIAL_SWEEP, dbToAngle(settings.gainDb))} fill="none" stroke="url(#adjustGainGrad)" strokeWidth="10" strokeLinecap="round" />
                        )}
                        <circle className="adjust-dial-knob" cx={knob.x} cy={knob.y} r="11" fill="#2563EB" stroke="#ffffff" strokeWidth="3" />
                        <text x={DIAL_SIZE / 2} y={DIAL_SIZE / 2 + 6} textAnchor="middle" className="adjust-dial-value">{formatDb(settings.gainDb)}</text>
                        <text x={DIAL_SIZE / 2} y={DIAL_SIZE / 2 + 26} textAnchor="middle" className="adjust-dial-unit">dB</text>
                    </svg>
                    <Icon className={`adjust-side-icon ${settings.gainDb > 0 ? 'hot' : ''}`} icon="lucide:volume-2" width="22" height="22" />
                </div>

                <div className="adjust-ruler">
                    <div className="adjust-ruler-ticks">
                        {RULER_TICKS.map((tick) => (
                            <i
                                key={tick}
                                className={`${RULER_LABELS.includes(tick) ? 'major' : ''} ${Math.abs(tick - settings.gainDb) < RULER_STEP / 2 ? 'active' : ''}`}
                            />
                        ))}
                    </div>
                    <input
                        type="range"
                        min={GAIN_MIN_DB}
                        max={GAIN_MAX_DB}
                        step="1"
                        value={settings.gainDb}
                        onChange={(e) => updateSetting('gainDb', Number(e.target.value))}
                        aria-label="Volume boost in dB"
                    />
                    <div className="adjust-ruler-labels">
                        {RULER_LABELS.map((label) => <span key={label}>{formatDb(label)}</span>)}
                    </div>
                </div>
            </div>

            <div className="adjust-card">
                <div className="adjust-card-head">
                    <div>
                        <h6 className="adjust-card-title">Playback Speed</h6>
                        <p className="adjust-card-subtitle">Change the speed of the audio</p>
                    </div>
                    <span className="adjust-speed-value">{formatSpeed(settings.speed)}</span>
                </div>

                <div className="adjust-speed-row">
                    <div className="adjust-speed-end"><Icon icon="lucide:turtle" width="18" height="18" /></div>
                    <input
                        type="range"
                        className="adjust-speed-slider"
                        min="0.5"
                        max="2"
                        step="0.05"
                        value={settings.speed}
                        onChange={(e) => updateSetting('speed', Number(e.target.value))}
                        style={{ background: `linear-gradient(to right, var(--tone) 0%, var(--tone) ${speedPercent}%, var(--vr-border) ${speedPercent}%, var(--vr-border) 100%)` }}
                        aria-label="Playback speed"
                    />
                    <div className="adjust-speed-end"><Icon icon="lucide:rabbit" width="18" height="18" /></div>
                </div>

                <div className="adjust-presets" role="group" aria-label="Speed presets">
                    {SPEED_PRESETS.map((preset) => (
                        <button key={preset} className={settings.speed === preset ? 'active' : ''} onClick={() => updateSetting('speed', preset)}>
                            {formatSpeed(preset)}
                        </button>
                    ))}
                </div>
            </div>

            <div className="adjust-actions">
                <button className="adjust-save vr-btn vr-btn-primary vr-btn-lg" disabled={!hasChanges || isLoading || isSaving} onClick={isLocalUpload ? saveLocalAsNew : saveAsNew}>
                    {isSaving ? (
                        <div className="vr-spinner adjust-save-spinner" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    ) : <><Icon icon="lucide:save" width="18" height="18" />Save</>}
                </button>
            </div>

        </div>
    );
};

export default AdjustAudio;
